import {
  breakpointKeys,
  narrowerBreakpointKeys,
} from 'src/js/modules/element-settings/constants/breakpoints';
import { targetClasses } from 'src/js/modules/element-settings/constants/target-classes';
import createBatch from 'src/js/modules/element-settings/helpers/create-batch';
import combineBatches from 'src/js/modules/element-settings/helpers/combine-batches';
import convertPlainBatchToBreakpointBasedBatch from 'src/js/modules/element-settings/helpers/convert-plain-batch-to-breakpoint-based-batch';
import hasWiderBreakpointValue from 'src/js/modules/element-settings/verifiers/has-wider-breakpoint-value';
import paddingDefaults from '../constants/padding-defaults';

// We need two separate values to correctly display and distinguish between the values set by the toggle and the slider, because a zero from either one looks the same.
//
// The slider writes `0px`. Turning Spacing off writes a unitless `0`, or removes the value when there is nothing to override.
//
// This distinction can be removed once element settings use accordions instead of toggles for option groups.
export const SPACING_OFF_VALUE = '0';

const autoPaddingTargetByPaddingTarget = {
  [targetClasses.PADDING_TOP]: targetClasses.AUTO_PADDING_TOP,
  [targetClasses.PADDING_BOTTOM]: targetClasses.AUTO_PADDING_BOTTOM,
  [targetClasses.COL_PADDING_LEFT]: targetClasses.AUTO_COL_PADDING_LEFT,
  [targetClasses.COL_PADDING_RIGHT]: targetClasses.AUTO_COL_PADDING_RIGHT,
};

const createBreakpointBatch = ({ elementId, target, breakpointKey, value }) =>
  convertPlainBatchToBreakpointBasedBatch(
    createBatch({ elementIds: [elementId], targets: [target], value }),
    breakpointKey
  );

const getElementTargetPairs = (elementIds, targets) =>
  Object.entries(elementIds).flatMap(([controlId, ids]) =>
    ids.flatMap((elementId) => (targets[controlId] || []).map((target) => ({ elementId, target })))
  );

const getSpacingOffValue = ({
  state,
  elementId,
  target,
  breakpointKey,
  isDefaultBreakpointKey,
}) => {
  if (isDefaultBreakpointKey) {
    return undefined;
  }

  return hasWiderBreakpointValue({ state, elementId, target, breakpointKey })
    ? SPACING_OFF_VALUE
    : undefined;
};

const getBatchesToClearNarrowerOffValues = ({ state, elementId, target }) =>
  narrowerBreakpointKeys[breakpointKeys.DEFAULT]
    .filter((breakpointKey) => state[elementId]?.[target]?.[breakpointKey] === SPACING_OFF_VALUE)
    .map((breakpointKey) =>
      createBreakpointBatch({ elementId, target, breakpointKey, value: undefined })
    );

const hasSpacingEnabledAtBreakpoint = (state, elementId, target, breakpointKey) => {
  const value = state[elementId]?.[target]?.[breakpointKey];

  return value !== undefined && value !== SPACING_OFF_VALUE;
};

const getBatchesToWriteNarrowerAutoPaddings = ({ elementId, target, breakpointKey, state }) => {
  const autoTarget = autoPaddingTargetByPaddingTarget[target];

  if (!autoTarget) {
    return [];
  }

  return (narrowerBreakpointKeys[breakpointKey] || []).flatMap((narrowerBreakpointKey) => {
    if (hasSpacingEnabledAtBreakpoint(state, elementId, target, narrowerBreakpointKey)) {
      return [];
    }

    const batches = [
      createBreakpointBatch({
        elementId,
        target: autoTarget,
        breakpointKey: narrowerBreakpointKey,
        value: paddingDefaults[narrowerBreakpointKey][target],
      }),
    ];

    if (state[elementId]?.[target]?.[narrowerBreakpointKey] !== undefined) {
      batches.push(
        createBreakpointBatch({
          elementId,
          target,
          breakpointKey: narrowerBreakpointKey,
          value: undefined,
        })
      );
    }

    return batches;
  });
};

const getTargetBatches = ({
  elementId,
  target,
  defaultsByBreakpoint,
  breakpointKey,
  isEnabled,
  isDefaultBreakpointKey,
  state,
}) => {
  const targetBatches = Object.entries(defaultsByBreakpoint).flatMap(
    ([defaultsBreakpointKey, defaults]) => {
      if (!(target in defaults)) {
        return [];
      }

      const value = isEnabled
        ? defaults[target]
        : getSpacingOffValue({
            state,
            elementId,
            target,
            breakpointKey: defaultsBreakpointKey,
            isDefaultBreakpointKey,
          });

      return [
        createBreakpointBatch({
          elementId,
          target,
          breakpointKey: defaultsBreakpointKey,
          value,
        }),
      ];
    }
  );

  if (isEnabled) {
    return [
      ...targetBatches,
      ...getBatchesToWriteNarrowerAutoPaddings({ elementId, target, breakpointKey, state }),
    ];
  }

  if (!isDefaultBreakpointKey) {
    return targetBatches;
  }

  return [...targetBatches, ...getBatchesToClearNarrowerOffValues({ state, elementId, target })];
};

const buildSpacingBreakpointBatch = ({
  elementIds,
  targets,
  defaultsByBreakpoint,
  breakpointKey,
  isEnabled,
  state,
}) => {
  const isDefaultBreakpointKey = breakpointKey === breakpointKeys.DEFAULT;
  const batches = getElementTargetPairs(elementIds, targets).flatMap(({ elementId, target }) =>
    getTargetBatches({
      elementId,
      target,
      defaultsByBreakpoint,
      breakpointKey,
      isEnabled,
      isDefaultBreakpointKey,
      state,
    })
  );

  return combineBatches(batches);
};

export default buildSpacingBreakpointBatch;
